import { useEffect, useState } from "react"
import { Link, useParams } from "react-router-dom"
import { useNavigate } from "react-router-dom";
import {
  Calendar,
  Phone,
  ChevronRight,
  Heart,
  HeartPulse,
  Clock,
  CheckCircle,
  MessageSquare,
  User,
  Star,
  PlusCircle,
  Activity,
  Stethoscope,
  Zap,
} from "lucide-react"

import { Button } from "../../components/ui/button"
import { Card, CardContent, CardFooter } from "../../components/ui/card"
import { Badge } from "../../components/ui/badge"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs"
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../../components/ui/accordion"

// ==========================
//  WP API URL
// ==========================
const API = "/wp-json/wp/v2"

export default function Servicesdetail() {
  const { slug } = useParams()
  const [loading, setLoading] = useState(true)
  const [service, setService] = useState(null)
  const [post, setPost] = useState(null)
  const [testimonials, setTestimonials] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const navigate = useNavigate();


  // ==========================================
  // Fetch category → posts → ACF fields
  // ==========================================

  async function loadService() {
    try {
      // 1️⃣ Get category by slug
      const catRes = await fetch(`${API}/categories?slug=${slug}`);
      const catData = await catRes.json();

      // ❌ If no category found → go to 404
      if (!catData || !catData.length) {
        navigate("/404", { replace: true });
        return;
      }

      const category = catData[0];
      setService(category);

      // 2️⃣ Get the main service post
      const postRes = await fetch(`${API}/posts?categories=${category.id}&_embed`);
      const posts = await postRes.json();

      // ❌ If no posts found → go to 404
      if (!posts || !posts.length) {
        navigate("/404", { replace: true });
        return;
      }

      setPost(posts[0]);

      // 3️⃣ Fetch Testimonials
      const testiRes = await fetch(
        `https://pranaam.pranaamhospitals.in/wp-json/wp/v2/testimonials?categories=${category.id}&per_page=3`
      );
      const testiData = await testiRes.json();
      setTestimonials(testiData || []);

    const docRes = await fetch(
    `https://pranaam.pranaamhospitals.in/wp-json/wp/v2/doctors?categories=${category.id}&per_page=3&_embed=1`
    );

      const docData = await docRes.json();
      setDoctors(docData || []);

      setLoading(false);

    } catch (error) {
      console.log("API Error:", error);

      // ❌ On ANY API error → go to 404
      navigate("/404", { replace: true });
    }
  }


  useEffect(() => {
    loadService()
  }, [slug])

  if (loading) return <div className="p-10 text-center">Loading...</div>
  if (!post) return <div className="p-10 text-center">Service Not Found</div>

  const acf = post.acf || {}

  // ----------------------------------------------------
  // YOU CAN MAP ACF FIELDS HERE (match your WP structure)
  // ----------------------------------------------------

  
  return (
    <div className="min-h-screen bg-white">

      {/* ============================================
          HERO SECTION (Dynamic)
      ============================================ */}
      <section className="relative py-16 md:py-24 bg-gradient-to-r from-primary/5 to-primary/10">
        <div className="container px-4 mx-auto">
          <div className="grid items-center grid-cols-1 gap-12 md:grid-cols-2">

            {/* Dynamic Content */}
            <div className="max-w-lg">
              <Badge className="mb-4 px-3 py-1 bg-primary/10 text-primary border-primary/20">
              {acf.tag_icon && (
                <img className="w-4 h-4 mr-1" src={acf.tag_icon} alt={acf.tag_title || service.name} title={acf.tag_title || service.name} />
              )}
                {acf.tag_title || service.name}
              </Badge>

              <h1 className="mb-4 text-4xl font-bold tracking-tight md:text-5xl lg:text-6xl font-heading">
                {acf.hero_title || post.title.rendered}
              </h1>

              <p className="mb-8 text-lg text-gray-700">
                {acf.hero_description}
              </p>

              <div className="flex flex-wrap gap-4">
                <Button size="lg" className="gap-2 shadow-md font-heading" asChild>
                  <Link to="/book-appointment">
                    <Calendar className="w-5 h-5" />
                    Book an Appointment
                  </Link>
                </Button>
              </div>
            </div>

            {/* Banner Image */}
            <div className="relative">
              <div className="relative h-64 overflow-hidden rounded-lg shadow-xl md:h-96">
                <img
                  src={acf.hero_image || "/placeholder.svg?height=600&width=600&text=No+Image"}
                  alt={acf.tag_title}
                  className="object-cover w-full h-full"
                />
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* ============================================
          WHY CHOOSE US (ACF Repeater)
      ============================================ */}
      {acf.title?.length > 0 && (
        <section className="py-16 bg-white">
          <div className="container px-4 mx-auto">

            <div className="max-w-3xl mx-auto mb-12 text-center">
              <div className="inline-flex items-center px-4 py-2 mb-4 space-x-2 text-sm font-medium rounded-full bg-primary/10 text-primary">
                {acf.tag_icon && (
                    <img className="w-4 h-4 mr-1" src={acf.tag_icon} alt={acf.tag_title || service.name} title={acf.tag_title || service.name} />
                )}  
                <span>Why Choose Us</span>
              </div>
              <h2 className="mb-6 text-3xl font-bold md:text-4xl font-heading">
                {acf.title || "Why Choose Us"}
              </h2>

              <p className="text-lg text-gray-700">{acf.description}</p>
            </div>

            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {acf.why_choose_card_repeater.map((item, idx) => (
                <Card key={idx}>
                  <CardContent className="p-6">
                    <div className="flex items-center gap-4 mb-4">
                        <div className="p-3 rounded-full bg-primary/10">
                          <img className="w-4 h-4" src={item.icon || "/placeholder.svg?height=24&width=24&text=No+Image"} alt={item.title} title={item.title} />
                        </div>
                        <h3 className="text-xl font-bold font-heading">{item.title}</h3>
                    </div>
                    <p className="text-gray-700">{item.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
            {acf.service_caption && (
            <div className="p-6 mt-10 text-center bg-primary/5 rounded-lg">
              <p className="text-lg text-gray-700">
                {acf.service_caption || "Why Choose Us"}
              </p>
            </div>
            )}
          </div>
        </section>
      )}

      {/* ============================================
          TABS: Diagnostics & Treatments (Dynamic)
      ============================================ */}
      <section id="treatments" className="py-16 bg-gray-50">
        <div className="container px-4 mx-auto">

          <div className="max-w-3xl mx-auto mb-12 text-center">
            <div className="inline-flex items-center px-4 py-2 mb-4 space-x-2 text-sm font-medium rounded-full bg-primary/10 text-primary">
                <Stethoscope className="w-5 h-5" />
                <span>Our Services</span>
            </div>
            <h2 className="mb-6 text-3xl font-bold md:text-4xl font-heading">
              {acf.tab_title || "Services & Treatments"}
            </h2>
            <p className="text-lg text-gray-700">
                {acf.tab_description || "Services & Treatments"}
              </p>
          </div>

          <Tabs 
            defaultValue={acf.tab_repeater?.[0]?.tab_heading || ""} 
            className="w-full"
            >

            {/* TAB HEADERS */}
            <TabsList className="grid w-full grid-cols-2 mb-8">
                {(acf.tab_repeater || []).map((tab, idx) => (
                <TabsTrigger 
                    key={idx}
                    value={tab.tab_heading}
                    className="font-heading"
                >
                    {tab.tab_heading}
                </TabsTrigger>
                ))}
            </TabsList>


            {/* TAB CONTENT SECTIONS */}
            {(acf.tab_repeater || []).map((tab, tabIndex) => (
                <TabsContent key={tabIndex} value={tab.tab_heading}>
                
                <div className="grid grid-cols-1 gap-6 md:grid-cols-2">

                    {(tab.tab_details || []).map((item, cardIndex) => (
                    <Card key={cardIndex}>
                        <CardContent className="p-6">

                        {/* ICON + TITLE */}
                        <div className="flex items-center gap-4 mb-4">
                            <div className="p-3 rounded-full bg-primary/10">
                            <img
                                className="w-6 h-6"
                                src={item.tab_card_icon || "/placeholder.svg?height=24&width=24&text=No+Image"}
                                alt={item.tab_card_title}
                                title={item.tab_card_title}
                            />
                            </div>

                            <h3 className="text-xl font-bold font-heading">
                            {item.tab_card_title}
                            </h3>
                        </div>

                        {/* DESCRIPTION */}
                        <p className="mb-4 text-gray-700">
                            {item.tab_card_description}
                        </p>

                        {/* BULLET POINTS */}
                        <ul className="space-y-2">
                            {(item.tab_card_bullet_point_reapeater || []).map((bullet, bIndex) => (
                            <li key={bIndex} className="flex items-start gap-2">
                                <CheckCircle className="w-5 h-5 mt-0.5 text-primary shrink-0" />
                                <span>{bullet.tab_card_bulletpoint}</span>
                            </li>
                            ))}
                        </ul>

                        </CardContent>
                    </Card>
                    ))}

                </div>

                </TabsContent>
            ))}

            </Tabs>
        </div>
      </section>

      {/* Meet Our Specialists */}
      <section className="py-16 bg-white">
        <div className="container px-4 mx-auto">
          <div className="max-w-3xl mx-auto mb-12 text-center">
            <div className="inline-flex items-center px-4 py-2 mb-4 space-x-2 text-sm font-medium rounded-full bg-primary/10 text-primary">
              <User className="w-5 h-5" />
              <span>Our Specialists</span>
            </div>

            <h2 className="mb-6 text-3xl font-bold md:text-4xl font-heading">
              Meet Our {service?.name}
            </h2>

            <p className="text-lg text-gray-700">
              Our team of experienced specialists is dedicated to providing advanced and compassionate care.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3">
            {doctors?.map((doc) => (
              <Card key={doc.id} className="overflow-hidden transition-all hover:shadow-lg hover:translate-y-[-5px]">

                {/* Doctor Image */}
                <div className="relative h-64">
                  <img
                    src={
                      doc._embedded?.["wp:featuredmedia"]?.[0]?.source_url ||
                      "/placeholder.svg?height=400&width=400"
                    }
                    alt={doc.title?.rendered}
                    className="object-cover w-full h-full"
                  />
                </div>

                <CardContent className="p-6">
                  <h3 className="mb-1 text-xl font-bold font-heading">
                    {doc.title?.rendered}
                  </h3>

                  <p className="mb-3 text-primary">
                    {doc.acf?.designation}
                  </p>

                  <p className="mb-4 text-sm text-gray-600">
                    {doc.acf?.overview}
                  </p>
                </CardContent>

                <CardFooter className="px-6 py-4 bg-gray-50">
                  <Button className="w-full gap-2" asChild>
                    <Link to="/book-appointment">
                      <Calendar className="w-4 h-4" />
                      Book Appointment
                    </Link>
                  </Button>
                </CardFooter>

              </Card>
            ))}

          </div>

          {/* View More */}
          <div className="flex justify-center mt-8">
            <Button variant="outline" className="gap-2 font-heading" asChild>
              <Link to={`/doctors?speciality=${service?.slug}`}>
                View All {service?.name} Specialists
                <ChevronRight className="w-4 h-4" />
              </Link>
            </Button>
          </div>

        </div>
      </section>

      {/* Patient Testimonials */}
      <section className="py-16 bg-gray-50">
        <div className="container px-4 mx-auto">
          <div className="max-w-3xl mx-auto mb-12 text-center">
            <div className="inline-flex items-center px-4 py-2 mb-4 space-x-2 text-sm font-medium rounded-full bg-primary/10 text-primary">
              <MessageSquare className="w-5 h-5" />
              <span>Testimonials</span>
            </div>
            <h2 className="mb-6 text-3xl font-bold md:text-4xl font-heading">Patient Success Stories</h2>
            <p className="text-lg text-gray-700">
              Read about the experiences of patients who have received {acf.hero_title || post.title.rendered} at Pranaam Hospitals.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
            {testimonials?.map((item) => (
              <Card key={item.id} className="transition-all hover:shadow-md">
                <CardContent className="p-6">

                  {/* ⭐ Rating */}
                  <div className="flex mb-4">
                    {[...Array(item.acf?.patient_rating || 5)].map((_, i) => (
                      <Star key={i} className="w-4 h-4 text-primary fill-primary" />
                    ))}
                  </div>

                  {/* ⭐ Review */}
                  <p className="mb-6 italic">
                    &ldquo;{item.acf?.patient_review}&rdquo;
                  </p>

                  {/* ⭐ Author */}
                  <div className="flex items-center">
                    <div className="relative w-12 h-12 mr-4 overflow-hidden rounded-full">
                      <img
                        src={item.acf?.patient_image || "/placeholder.svg"}
                        alt={item.acf?.patient_name}
                        className="object-cover w-full h-full"
                      />
                    </div>

                    <div>
                      <h4 className="font-bold">{item.acf?.patient_name}</h4>
                      <p className="text-sm text-muted-foreground">
                        {item.acf?.treatment_related_}
                      </p>
                    </div>
                  </div>

                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* ============================================
          FAQ SECTION (ACF Repeater)
      ============================================ */}
       {acf.faq_repeater?.length > 0 && (
        <section className="py-16 bg-white">
          <div className="container px-4 mx-auto">

            <div className="max-w-3xl mx-auto mb-12 text-center">
              <div className="inline-flex items-center px-4 py-2 mb-4 space-x-2 rounded-full bg-primary/10 text-primary">
                <span>FAQs</span>
              </div>

              <h2 className="mb-6 text-3xl font-bold md:text-4xl font-heading">
                Frequently Asked Questions
              </h2>

              <p className="text-lg text-gray-700">
                {acf.description}
              </p>
            </div>

            <div className="max-w-3xl mx-auto">

              {acf.faq_repeater?.map((item, index) => (
                <Accordion type="single" collapsible className="w-full">
                  <AccordionItem value="item-1">
                    <AccordionTrigger className="text-left font-heading">
                      {item.question}
                    </AccordionTrigger>
                    <AccordionContent className="text-gray-700">
                      {item.answer}
                    </AccordionContent>
                  </AccordionItem>
                  </Accordion>
              ))}

            </div>

          </div>
        </section>

      )} 

    </div>
  )
}
